import {
  azimuthToUnitVector,
  makeFrame,
  type Vec2,
} from '@payaca/helpers/solarFacePacker';
import type { FacePolygon, LatLng } from '@payaca/helpers/solarFacePacker';

/**
 * The draw tool's quick-roof templates (Q cycles them while the tool is
 * armed): a parameterised whole-roof shape dropped from the same two-click
 * rectangle gesture, arriving as multiple faces that share ridge/hip edges.
 */
export type RoofTemplateKind = 'rect' | 'gable' | 'hip' | 'shed';
export type RoofDrawKind = 'outline' | RoofTemplateKind;

export const ROOF_TEMPLATE_CYCLE: RoofDrawKind[] = [
  'outline',
  'rect',
  'gable',
  'hip',
  'shed',
];

export const ROOF_TEMPLATE_LABELS: Record<RoofDrawKind, string> = {
  outline: 'Outline',
  rect: 'Rectangle',
  gable: 'Gable',
  hip: 'Hip',
  shed: 'Shed',
};

export type TemplateFace = {
  polygon: FacePolygon;
  /** Compass azimuth the face drains towards (outward from the ridge). */
  azimuthDegrees: number;
};

/**
 * Two vertices (face index + outer-ring vertex index) that are the same
 * physical point — a shared ridge/hip/eave corner. The designer welds these:
 * dragging either moves both.
 */
export type TemplateVertexLink = {
  faceA: number;
  vertexA: number;
  faceB: number;
  vertexB: number;
};

export type RoofTemplateResult = {
  faces: TemplateFace[];
  links: TemplateVertexLink[];
};

const normaliseAzimuth = (degrees: number): number =>
  ((degrees % 360) + 360) % 360;

/**
 * Every pair of coincident vertices across the template's faces, matched in
 * the metric frame. O(n²) over a handful of vertices; template shapes only.
 */
const coincidentLinks = (
  facesUv: Vec2[][],
  epsilonMeters = 1e-6
): TemplateVertexLink[] => {
  const links: TemplateVertexLink[] = [];
  for (let fa = 0; fa < facesUv.length; fa++) {
    for (let fb = fa + 1; fb < facesUv.length; fb++) {
      for (let va = 0; va < facesUv[fa].length; va++) {
        for (let vb = 0; vb < facesUv[fb].length; vb++) {
          const a = facesUv[fa][va];
          const b = facesUv[fb][vb];
          if (
            Math.abs(a.x - b.x) < epsilonMeters &&
            Math.abs(a.y - b.y) < epsilonMeters
          ) {
            links.push({ faceA: fa, vertexA: va, faceB: fb, vertexB: vb });
          }
        }
      }
    }
  }
  return links;
};

/**
 * Build a quick-roof template from the draw gesture's two opposite corners.
 *
 * Same frame as `buildOrientedRectangle`: `azimuthDegrees` is the drainage
 * direction sampled under the first click (or the default), v runs downhill
 * along it, u runs along the ridge (cross-slope). The footprint is the
 * axis-aligned u/v rectangle through both corners; the RIDGE runs along u at
 * mid-v — so the face the user started drawing on drains the sampled way and
 * the opposite face drains the other way:
 *
 *  - rect — one face, the plain rectangle (the draw tool's existing shape).
 *  - shed — one face covering the footprint, single drainage.
 *  - gable — two faces split at the ridge, draining apart.
 *  - hip — two trapezoids + two end triangles; the ridge is inset from each
 *    end by the half-width (45° hip creases), clamped so a short footprint
 *    keeps a real ridge instead of degenerating to a pyramid.
 *
 * Vertex links mark every shared physical point (ridge ends, hip corners,
 * shared eave corners) for the designer to weld.
 */
export const buildRoofTemplate = (
  a: LatLng,
  b: LatLng,
  azimuthDegrees: number,
  kind: RoofTemplateKind
): RoofTemplateResult => {
  const frame = makeFrame(a);
  const am = frame.toM(a);
  const bm = frame.toM(b);
  const downhill = azimuthToUnitVector(azimuthDegrees);
  const crossSlope = { x: downhill.y, y: -downhill.x };
  const project = (p: Vec2) => ({
    u: p.x * crossSlope.x + p.y * crossSlope.y,
    v: p.x * downhill.x + p.y * downhill.y,
  });
  const pa = project(am);
  const pb = project(bm);
  const u0 = Math.min(pa.u, pb.u);
  const u1 = Math.max(pa.u, pb.u);
  const v0 = Math.min(pa.v, pb.v);
  const v1 = Math.max(pa.v, pb.v);
  const vMid = (v0 + v1) / 2;

  const toGround = (uv: { u: number; v: number }): Vec2 => ({
    x: uv.u * crossSlope.x + uv.v * downhill.x,
    y: uv.u * crossSlope.y + uv.v * downhill.y,
  });

  // Face azimuths: +v is the sampled drainage; -v its opposite; ±u the two
  // hip-end directions (u is the downhill axis rotated 90° anticlockwise).
  const azPlusV = normaliseAzimuth(azimuthDegrees);
  const azMinusV = normaliseAzimuth(azimuthDegrees + 180);
  const azPlusU = normaliseAzimuth(azimuthDegrees + 90);
  const azMinusU = normaliseAzimuth(azimuthDegrees - 90);

  let facesUv: { ring: { u: number; v: number }[]; azimuthDegrees: number }[];

  switch (kind) {
    case 'rect':
    case 'shed': {
      facesUv = [
        {
          ring: [
            { u: u0, v: v0 },
            { u: u1, v: v0 },
            { u: u1, v: v1 },
            { u: u0, v: v1 },
          ],
          azimuthDegrees: azPlusV,
        },
      ];
      break;
    }
    case 'gable': {
      facesUv = [
        // The +v half — the slope the user started on.
        {
          ring: [
            { u: u0, v: vMid },
            { u: u1, v: vMid },
            { u: u1, v: v1 },
            { u: u0, v: v1 },
          ],
          azimuthDegrees: azPlusV,
        },
        // The -v half, draining the opposite way.
        {
          ring: [
            { u: u0, v: v0 },
            { u: u1, v: v0 },
            { u: u1, v: vMid },
            { u: u0, v: vMid },
          ],
          azimuthDegrees: azMinusV,
        },
      ];
      break;
    }
    case 'hip': {
      // 45° hip creases: ridge inset = half-width, clamped to keep a real
      // ridge (≥10% of the u-span) on stubby footprints.
      const inset = Math.min((v1 - v0) / 2, (u1 - u0) * 0.45);
      const r0 = u0 + inset;
      const r1 = u1 - inset;
      facesUv = [
        // +v trapezoid (the slope the user started on).
        {
          ring: [
            { u: r0, v: vMid },
            { u: r1, v: vMid },
            { u: u1, v: v1 },
            { u: u0, v: v1 },
          ],
          azimuthDegrees: azPlusV,
        },
        // -v trapezoid.
        {
          ring: [
            { u: u0, v: v0 },
            { u: u1, v: v0 },
            { u: r1, v: vMid },
            { u: r0, v: vMid },
          ],
          azimuthDegrees: azMinusV,
        },
        // +u end triangle.
        {
          ring: [
            { u: u1, v: v0 },
            { u: u1, v: v1 },
            { u: r1, v: vMid },
          ],
          azimuthDegrees: azPlusU,
        },
        // -u end triangle.
        {
          ring: [
            { u: u0, v: v1 },
            { u: u0, v: v0 },
            { u: r0, v: vMid },
          ],
          azimuthDegrees: azMinusU,
        },
      ];
      break;
    }
  }

  const facesUvVecs = facesUv.map((face) =>
    face.ring.map((uv) => {
      const ground = toGround(uv);
      return { x: ground.x, y: ground.y };
    })
  );

  return {
    faces: facesUv.map((face, index) => ({
      polygon: {
        outer: facesUvVecs[index].map((vec) => frame.toLatLng(vec)),
        holes: [],
      },
      azimuthDegrees: face.azimuthDegrees,
    })),
    links: coincidentLinks(facesUvVecs),
  };
};
